import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { ColumnDirective, ColumnsDirective, TreeGridComponent } from '@syncfusion/ej2-react-treegrid';
import React, { useEffect, useRef, useState } from 'react';
import OntologyActions from '../actions/OntologyActions';
import FacilityStore from '../stores/FacilityStore';
import OperationProgressDialog from './OperationProgressDialog';
import * as Inovx from '@shared';
interface GraphicContentProps {
    hierarchy: IFacilityHierarchyPart;
}
var SystemActions = Inovx.Shared.System.Actions;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";
const hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);
const graphicSourceTypeMap: Record<number, string> = {
    1: "View360",
    2: "Image360",
    3: "CADData",
    4: "AssetData",
    5: "RegionData",
    6: "FBXCADData"
};

const mediaGalleryGraphicSourceTypes = new Set([1, 2]);

function createMediaGalleryLinkRow(type: number, ontologyID: string): IOntologyGraphicSource & { IsMediaGalleryLink: boolean } {
    return {
        Id: `media-gallery-${ontologyID}-${type}`,
        ID: `media-gallery-${ontologyID}-${type}`,
        Name: InovxLoader.t('facility:menu.mediaGallery'),
        Type: type,
        VersionToken: '',
        ontologyID,
        IsMediaGalleryLink: true
    };
}

let ontologyId: string = '';
const GraphicContent: React.FC<GraphicContentProps> = ({ hierarchy }) => {
    const treeGridRef = useRef<TreeGridComponent | null>(null);
    const [graphicSources, setGraphicSources] = useState<any>([]);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const getData = async () => {
        const data = await OntologyActions.getOntologyGraphicSources(ontologyId);
       
        const grouped = Object.values(
            data.reduce((acc:any, item) => {
                if (!acc[item.Type]) {
                    acc[item.Type] = {
                        Id: item.Id,
                        ID: item.ID || item.Id,
                        Type: item.Type,
                        Name: graphicSourceTypeMap[item.Type],
                        VersionToken: item.VersionToken,
                        Children: []
                    };
                }
                acc[item.Type].Children.push({...item, ontologyID: ontologyId });
                return acc;
            }, {} as IOntologyGraphicSource)
        ).map((source: IOntologyGraphicSources) => {
            if (!mediaGalleryGraphicSourceTypes.has(source.Type)) {
                return source;
            }

            return {
                ...source,
                SourceCount: source.Children.length,
                Children: [
                    createMediaGalleryLinkRow(source.Type, ontologyId)
                ]
            };
        });

        setGraphicSources(grouped)
    }
    useEffect(() => {
        if (hierarchy?.ID) {
            ontologyId = hierarchy.ID;
            getData();
        }
    }, [hierarchy]);

    const renderGraphicSourceCount = (Type: number, SourceCount?: number) => {
        if (!mediaGalleryGraphicSourceTypes.has(Type) || SourceCount == null) {
            return null;
        }

        const imageCountLabel = SourceCount === 1
            ? InovxLoader.t('facility:graphicSource.image')
            : InovxLoader.t('facility:graphicSource.images');

        return (
            <span className="ml-5p">
                ({SourceCount.toLocaleString()} {imageCountLabel})
            </span>
        );
    }

    const renderGraphicSourceTitle = (Type: number, SourceCount?: number) => {
        if (Type === 1) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.scanPosition')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.panoImages')}</span>
                {renderGraphicSourceCount(Type, SourceCount)}
            </span>)
        } else if (Type === 2) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.image360')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.panoImages')}</span>
                {renderGraphicSourceCount(Type, SourceCount)}
            </span>)
        } else if (Type === 3) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.glb')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.cadData')}</span>
            </span>)
        } else if (Type === 6) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.fbx')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.cadData')}</span>
            </span>)
        } else if (Type === 4) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.vnf')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.assetData')}</span>
            </span>)
        } else if (Type === 5) {
            return (<span>
                <span className="font-bold">{InovxLoader.t('facility:graphicSource.pointCloud')}</span>
                <span className="ml-5p">{InovxLoader.t('facility:graphicSource.regionData')}</span>
            </span>)
        }
    }

    const renderMediaGalleryLink = () => {
        return (
            <a className="graphic-content-media-gallery-link" onClick={(event) => { event.preventDefault(); openMediaGallery(); }} href="#">
                {InovxLoader.t('facility:graphicSource.manageContentIn')} {InovxLoader.t('facility:menu.mediaGallery')}
            </a>
        );
    }

    const handleDelete = (data: IOntologyGraphicSource): void => {
        if (data) {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmGraphicSourceDeletion'),
                confirmCallback: async function () {
                    setOperationMessage(InovxLoader.t('facility:message.deletingGraphicContent'));
                    try {
                        await OntologyActions.deleteOntologyGraphicSource(data);
                        await getData();
                    } finally {
                        setOperationMessage(null);
                    }
                }
            });
        }
    }
    const handleDeleteSourceType = (data: IOntologyGraphicSourceType): void => {
        if (data) {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmGraphicSourceTypeDeletion'),
                confirmCallback: async function () {
                    setOperationMessage(InovxLoader.t('facility:message.deletingGraphicContent'));
                    try {
                        await OntologyActions.deleteOntologyGraphicSourceType(data);
                        await getData();
                    } finally {
                        setOperationMessage(null);
                    }
                }
            });
        }
    }
    function handleDeleteClick(event?: React.MouseEvent, isRoot: boolean = false): void {
        event?.stopPropagation();
        const rowInfo:any = treeGridRef.current.getRowInfo(event?.target as HTMLImageElement);
        if (rowInfo?.rowData && !rowInfo.rowData.IsMediaGalleryLink) {
            if (isRoot) {
                let params: IOntologyGraphicSourceType = { ontologyID: ontologyId, sourceType: rowInfo.rowData?.Type };
                handleDeleteSourceType(params);
            } else {
                handleDelete(rowInfo.rowData as IOntologyGraphicSource);
            }
        }
    }

    function openMediaGallery(): void {
        const url = new URL(window.location.href);
        const currentFacilityId = url.searchParams.get('facilityId') || FacilityStore.getSelectedFacility()?.ID;

        url.pathname = '/Facility';
        url.searchParams.set('mode', 'media');
        url.searchParams.set('ontologyId', hierarchy.ID);

        if (currentFacilityId) {
            url.searchParams.set('facilityId', currentFacilityId);
        }

        window.location.href = url.toString();
    }

    return (<div style={{ padding: '16px 0' }}>
        <OperationProgressDialog visible={!!operationMessage} message={operationMessage || undefined} />
        <TreeGridComponent
            ref={treeGridRef}
            dataSource={graphicSources}
            childMapping="Children"
            treeColumnIndex={0}
            height="100%"
            rowHeight={36}
            allowSelection={false}
            selectionSettings={{ cellSelectionMode: 'Flow', mode: 'Row', type: 'Single', enableToggle: false }}
        >
            <ColumnsDirective>
                <ColumnDirective
                    field="Name"
                    headerText={InovxLoader.t("facility:hierarchy.nameColumn")}
                    width="200"
                    template={(props: any) => {
                        const isRoot = !props.parentItem;
                        return (
                            <div className="facility-hierarchy-treegrid-name">
                                <div className="facility-hierarchy-treegrid-name-label">
                                    {isRoot ? renderGraphicSourceTitle(props.Type, props.SourceCount) : props.IsMediaGalleryLink ? (
                                        renderMediaGalleryLink()
                                    ) : props.Name}
                                </div>
                            </div>
                        )
                    }} />
                <ColumnDirective field="ID" headerText="ID" visible={false} width="0" isPrimaryKey={true} />
                {hasManagePermission && <ColumnDirective field="Actions" headerText="" width="60" template={(props: any) => {
                    const isRoot = !props.parentItem;
                    return (
                        props.IsMediaGalleryLink || (props.Type == 4 && !isRoot) ? <></> : (
                            <span className="facility-hierarchy-actions j-ce">
                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/Bin.png`} className='facility-hierarchy-delete-icon delete-icon' title={InovxLoader.t('facility:form.delete')} onClick={e => { handleDeleteClick(e, isRoot); }} />
                            </span>
                        )
                    );
                }} />
                }
            </ColumnsDirective>
        </TreeGridComponent>
    </div>)
};

export default GraphicContent;
